import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiEye, mdiInformation, mdiScriptTextPlay } from "@mdi/js";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute, Link as RouterLink } from "@tanstack/react-router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MaintenanceWindow } from "../../../../@types/services/MaintenanceWindow";
import { listServiceQuotationsForIssuance } from "../../../../__generated__/orval/endpoints/service-quotations/service-quotations";
import type { EntitiesServiceQuotationIssueListResponseOutput } from "../../../../__generated__/orval/models";
import {
  createCreatePurchaseOrderByServiceQuotation,
  listCreatePurchaseOrderByServiceQuotations,
} from "../../../../services/createPurchaseOrderByServiceQuotations";
import { fetchMaintenanceWindows } from "../../../../services/maintenanceWindows";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";

type DataTabProps = {
  valid: boolean;
  selected: number[];
  selectedCallback: React.Dispatch<React.SetStateAction<number[]>>;
};

type IssueSummaryRow =
  EntitiesServiceQuotationIssueListResponseOutput["results"][number] & {
    has_requester_approval?: boolean;
  };

type IssueSummaryPrItem = {
  material?: {
    matnumber?: string;
  };
  requisition_date?: string;
};

const extractIssueSummaryPrItem = (value: unknown): IssueSummaryPrItem => {
  if (typeof value !== "object" || value === null) {
    return {};
  }

  const record = value as Record<string, unknown>;
  const materialValue = record.material;
  const material =
    typeof materialValue === "object" && materialValue !== null
      ? (materialValue as Record<string, unknown>)
      : null;

  return {
    material: material
      ? {
          matnumber:
            typeof material.matnumber === "string"
              ? material.matnumber
              : undefined,
        }
      : undefined,
    requisition_date:
      typeof record.requisition_date === "string"
        ? record.requisition_date
        : undefined,
  };
};

const DataTab: React.FC<DataTabProps> = ({
  valid,
  selected,
  selectedCallback,
}) => {
  const { t } = useTranslation();

  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [serviceQuotations, setServiceQuotations] = useState<IssueSummaryRow[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const maximumSelectedItems = 100;

  const handleFetchServiceQuotations = useCallback(
    async (page: number) => {
      setLoading(() => true);
      await listServiceQuotationsForIssuance({
        items_per_page: rowsPerPage,
        order: "desc",
        page,
        valid,
      })
        .then((serviceQuotation) => {
          setServiceQuotations(() => serviceQuotation.results || []);
          setTotalRows(() => serviceQuotation.total_items || 0);
        })
        .finally(() => {
          setLoading(() => false);
        });
    },
    [rowsPerPage, valid],
  );

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    value: number,
  ): void => {
    setCurrentPage(() => value + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setRowsPerPage(() => parseInt(event.target.value, 10));
    setCurrentPage(() => 1);
  };

  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (event.target.checked) {
      let newSelected: number[] = [];
      newSelected = newSelected.concat(selected);

      serviceQuotations.forEach((serviceQuotation) => {
        if (
          newSelected.length < maximumSelectedItems &&
          !newSelected.includes(serviceQuotation.id)
        ) {
          newSelected.push(serviceQuotation.id);
        }
      });

      selectedCallback(() => newSelected);
      return;
    }

    selectedCallback(() =>
      selected.filter(
        (el) =>
          !serviceQuotations
            .map((serviceQuotation) => serviceQuotation.id)
            .includes(el),
      ),
    );
  };

  const handleSelectClick = (
    _event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    id: number,
  ): void => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      if (selected.length >= maximumSelectedItems) {
        newSelected = selected;
      } else {
        newSelected = newSelected.concat(selected, id);
      }
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    selectedCallback(() => newSelected);
  };

  const isTableRowSelected = (id: number): boolean => {
    return selected.indexOf(id) !== -1;
  };

  useEffect(() => {
    handleFetchServiceQuotations(currentPage);
  }, [currentPage, handleFetchServiceQuotations]);

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={
                  serviceQuotations.length > 0 &&
                  selected.length === serviceQuotations.length
                }
                color="primary"
                indeterminate={
                  selected.length > 0 &&
                  selected.length !== serviceQuotations.length
                }
                onChange={handleSelectAllClick}
              />
            </TableCell>
            <TableCell align="left">
              {t("serviceQuotation.identifier")}
            </TableCell>
            <TableCell align="left">
              {t("serviceQuotation.requisitionDate")}
            </TableCell>
            <TableCell align="left">{t("serviceQuotation.service")}</TableCell>
            <TableCell align="left">{t("serviceQuotation.vendor")}</TableCell>
            <TableCell align="center" />
          </TableRow>
        </TableHead>
        <TableBody>
          {!!loading &&
            Array.from(
              { length: rowsPerPage > 10 ? 10 : rowsPerPage },
              (_, i) => `loading-skeleton-${i}`,
            ).map((rowKey) => (
              <TableRow key={rowKey}>
                {Array.from({ length: 6 }, (_, j) => `${rowKey}-cell-${j}`).map(
                  (cellKey) => (
                    <TableCell key={cellKey}>
                      <Skeleton />
                    </TableCell>
                  ),
                )}
              </TableRow>
            ))}
          {!loading &&
            serviceQuotations?.length > 0 &&
            serviceQuotations.map((serviceQuotation) => {
              const prItem = extractIssueSummaryPrItem(
                serviceQuotation.pr_item,
              );

              const missingDocuments = [
                !serviceQuotation.has_requester_approval &&
                  t("serviceQuotation.attachmentsSection.requester_approval"),
                !serviceQuotation.has_ssu_checklist &&
                  t("serviceQuotation.attachmentsSection.ssu_checklist"),
                serviceQuotation.is_single_sourcing &&
                  !serviceQuotation.has_single_sourcing_justification &&
                  t(
                    "serviceQuotation.attachmentsSection.single_sourcing_justification",
                  ),
              ].filter(Boolean) as string[];

              const hasMissingDocuments = missingDocuments.length > 0;

              return (
                <React.Fragment key={serviceQuotation.id}>
                  <TableRow
                    aria-checked={isTableRowSelected(serviceQuotation.id)}
                    hover
                    onClick={(
                      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
                    ) => handleSelectClick(event, serviceQuotation.id)}
                    role="checkbox"
                    selected={isTableRowSelected(serviceQuotation.id)}
                    sx={{ cursor: "pointer" }}
                    tabIndex={-1}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isTableRowSelected(serviceQuotation.id)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>{serviceQuotation.identifier}</TableCell>
                    <TableCell>
                      {prItem.requisition_date
                        ? t(
                            "intlDate",
                            i18nextParseToDate(prItem.requisition_date),
                          )
                        : "-"}
                    </TableCell>
                    <TableCell>{prItem.material?.matnumber}</TableCell>
                    <TableCell>
                      {serviceQuotation.vendor_winner?.fullvendorname}
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title={t("view")}>
                        <IconButton
                          component={Link}
                          // to={`/vbuyer/quotations/${quotation.id}`}
                          href={`/vbuyer/service_quotations/${serviceQuotation.id}`}
                          target="_blank"
                        >
                          <Icon icon={mdiEye} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {hasMissingDocuments && (
                    <TableRow
                      key={`missing-docs-${serviceQuotation.id}`}
                      sx={{ "& > td": { py: 0.5 } }}
                    >
                      <TableCell colSpan={6}>
                        <Alert severity="warning" sx={{ py: 0.25 }}>
                          {t("issueServicePurchaseOrder.missingDocuments", {
                            documents: missingDocuments.join(", "),
                          })}
                        </Alert>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={2}>
              <Box sx={{ alignItems: "center", display: "flex" }}>
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    paddingRight: 1,
                  }}
                >
                  <Tooltip
                    title={t("issueServicePurchaseOrder.maximumSelectedItems", {
                      count: maximumSelectedItems,
                    })}
                  >
                    <Icon icon={mdiInformation} />
                  </Tooltip>
                </Box>
                <Typography>
                  {t("rowSelected", { count: selected.length })}
                </Typography>
              </Box>
            </TableCell>
            <TablePagination
              count={totalRows}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={currentPage - 1}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              showFirstButton
              showLastButton
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

const IssuingPurchaseOrder: React.FC = () => {
  const { t } = useTranslation();

  const [maintenanceWindows, setMaintenanceWindows] = useState<
    MaintenanceWindow[]
  >([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loadingGeneratePurchaseOrder, setLoadingGeneratePurchaseOrder] =
    useState(false);
  const [tabOpen, setTabOpen] = useState("valid");
  const [hasIncompleteOrders, setHasIncompleteOrders] = useState(false);

  useEffect(() => {
    const checkIncompleteOrders = async (): Promise<void> => {
      try {
        const response = await listCreatePurchaseOrderByServiceQuotations({
          filterCompleted: false,
          itemsPerPage: 1,
          order: "desc",
          page: 1,
        });

        if (response.results && response.results.length > 0) {
          setHasIncompleteOrders(true);
        }
      } catch (error) {
        console.error("Error checking incomplete orders:", error);
      }
    };

    checkIncompleteOrders();
  }, []);

  const didInit = useRef(false);

  const hasMaintenanceWindows = maintenanceWindows.length > 0;

  const handleGeneratePurchaseOrder = (): void => {
    if (hasMaintenanceWindows) {
      return;
    }
    setLoadingGeneratePurchaseOrder(() => true);

    createCreatePurchaseOrderByServiceQuotation(selected)
      .then((createPurchaseOrderByServiceQuotation) => {
        window.location.href = `/vbuyer/api/v2/create_purchase_order_by_service_quotations/${createPurchaseOrderByServiceQuotation.id}/generate_purchase_order`;
      })
      .finally(() => {
        setLoadingGeneratePurchaseOrder(() => false);
      });
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ): void => {
    setTabOpen(() => newValue);
  };

  const handleFetchMaintenanceWindows = useCallback(async () => {
    const response = await fetchMaintenanceWindows({
      filters: { active: true, system: "sap_p03" },
    });
    setMaintenanceWindows(
      response.results.filter(
        (maintenanceWindow) =>
          maintenanceWindow.blocked_actions.length === 0 ||
          maintenanceWindow.blocked_actions.includes(
            "issue_service_purchase_order",
          ),
      ),
    );
  }, []);

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      handleFetchMaintenanceWindows();
    }
  }, [handleFetchMaintenanceWindows]);

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
        justifyContent: "center",
        px: 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <h1>{t("issueServicePurchaseOrder.title")}</h1>

        <Box
          sx={{
            alignItems: "center",
            display: "flex",
          }}
        >
          <Button
            component={RouterLink}
            to="/vbuyer/create_purchase_order_by_service_quotations"
          >
            <Icon icon={mdiScriptTextPlay} />
            <span>{t("issueServicePurchaseOrder.trackExecutions")}</span>
          </Button>
        </Box>
      </Box>

      {hasMaintenanceWindows &&
        maintenanceWindows.map((maintenanceWindow) => (
          <Alert key={maintenanceWindow.id} severity="warning" sx={{ mb: 2 }}>
            {maintenanceWindow.message}
          </Alert>
        ))}

      {!!hasIncompleteOrders && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="/vbuyer/create_purchase_order_by_service_quotations"
            rel="noreferrer"
            target="_blank"
            underline="hover"
          >
            {t("createPurchaseOrderByServiceQuotation.incompleteOrdersError")}
          </Link>
        </Alert>
      )}

      {loadingGeneratePurchaseOrder ? (
        <Button color="info" variant="outlined">
          <CircularProgress
            color="success"
            size={16}
            sx={{ marginRight: "5px" }}
          />
          <span>{t("issueServicePurchaseOrder.generatingPurchaseOrder")}</span>
        </Button>
      ) : (
        <Button
          disabled={selected.length <= 0 || hasMaintenanceWindows}
          onClick={handleGeneratePurchaseOrder}
          type="button"
          variant="outlined"
        >
          {t("issueServicePurchaseOrder.generatePurchaseOrder")}
        </Button>
      )}

      <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
        {t("issueServicePurchaseOrder.tdiApprovalWarning")}
      </Alert>

      <TabContext value={tabOpen}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs onChange={handleTabChange} value={tabOpen}>
            <Tab
              label={t("issueServicePurchaseOrder.quotationsValid")}
              value="valid"
            />
            <Tab
              label={t("issueServicePurchaseOrder.quotationsInvalid")}
              value="invalid"
            />
          </Tabs>
        </Box>

        <TabPanel value="valid">
          <DataTab selected={selected} selectedCallback={setSelected} valid />
        </TabPanel>
        <TabPanel value="invalid">
          <DataTab
            selected={selected}
            selectedCallback={setSelected}
            valid={false}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/service_quotations/issue_service_purchase_order",
)({
  component: IssuingPurchaseOrder,
});
